// externals
import type OktaAuth from '@okta/okta-auth-js';

// utils
import { setOktaAuthForFetchInterceptor, setupAuthFetchIntercept } from './fetchIntercept';
import { setOktaAuthForAxiosInterceptor, setupAuthAxiosIntercept } from './axiosIntercept';
import type { ApiUrlBuilder } from './types';
import { logNewRelicPageAction } from '@makemydeal/dr-activities-common';

let authOkta: OktaAuth;
let tokenRenewalCallback: (() => Promise<boolean>) | undefined;

/**
 * Register Fetch and Axios intercepts to add auth headers for all client network requests.
 * @returns void
 */
export const setupAuthNetworkIntercepts = (appPrefix: string) => {
    setupAuthFetchIntercept(appPrefix);
    setupAuthAxiosIntercept(appPrefix);
};

export const setupAuthNetworkInterceptsForModule = (apiUrlBuilder: ApiUrlBuilder) => {
    setupAuthFetchIntercept(apiUrlBuilder);
    setupAuthAxiosIntercept(apiUrlBuilder);
};

export const setOktaAuthForInterceptors = (oktaAuth: OktaAuth) => {
    setOktaAuthForFetchInterceptor(oktaAuth);
    setOktaAuthForAxiosInterceptor(oktaAuth);
    authOkta = oktaAuth;
};

/**
 * Register a callback function that will be called when token renewal is needed.
 * This should be called from the auth manager component.
 */
export const registerTokenRenewalCallback = (callback: () => Promise<boolean>) => {
    tokenRenewalCallback = callback;
};

/**
 * Check if an access token is already expired.
 * Note: Does NOT check if expiring soon - that's handled by the auth manager's
 * 'expired' event listener. This only checks for actual expiration to avoid
 * race conditions with the existing renewal mechanism.
 */
const isTokenExpired = async (): Promise<boolean> => {
    try {
        const accessToken = await authOkta.tokenManager.get('accessToken');
        if (!accessToken) return true;

        const now = Math.floor(Date.now() / 1000);
        const expiresAt = accessToken.expiresAt || 0;

        // Only return true if token is ALREADY expired (not expiring soon)
        // This prevents race conditions with the auth manager's 'expired' event handler
        return now >= expiresAt;
    } catch (err) {
        return true;
    }
};

export const getOktaToken = () => {
    return authOkta && authOkta.getAccessToken ? authOkta.getAccessToken() : undefined;
};

/**
 * Get the current Okta access token. If the token is already expired,
 * it will attempt to renew it using the registered renewal callback.
 *
 * Note: This only handles ACTUAL expiration (not preemptive renewal).
 * Preemptive renewal is handled by the auth manager's 'expired' event listener
 * which fires 60 seconds before expiry. This prevents race conditions.
 * Auth managers event listener sometimes fails to renew the token in time
 * (in some edge cases usually when machine is in sleep mode)
 *
 * @returns Promise<string | undefined> - The access token or undefined if not available
 */
export const getOktaTokenAsync = async (): Promise<string | undefined> => {
    // Only check token expiry if feature toggle is enabled
    if (!authOkta || !authOkta.getAccessToken) return undefined;

    // Check if token is ALREADY expired (not expiring soon)
    const expired = await isTokenExpired();

    if (expired && tokenRenewalCallback) {
        logTokenEventsToNR('TokenExpired', 'Access token is expired, attempting renewal');
        try {
            const renewed = await tokenRenewalCallback();
            renewed
                ? logTokenEventsToNR('TokenRenewalSuccess', 'Access token renewed successfully')
                : logTokenEventsToNR('TokenRenewalFailed', 'Access token renewal failed');
        } catch (err) {
            logTokenEventsToNR('TokenRenewalError', 'Token renewal error');
        }
    }

    return authOkta.getAccessToken();
};

const logTokenEventsToNR = (action: string, message: string) => {
    logNewRelicPageAction('MV:AccessToken', {
        action,
        message
    });
};
